import { Pog, PogNonCircular } from "@CommonTypes/merchflow/pog/pog";
import type { PlanogramItemCommonNonCircular } from "@CommonTypes/merchflow/pog/item";
import type { GeneratedLayout, StudioItem } from "../domain/studioTypes";
import { wallRackSlotOf, wallRowRankOf } from "@CommonUtils/cdtStudio/adapters/pogToStudioItems";
import {
  getEffectiveMerchandisableWidth,
  getLiveSpans,
  type ShelfWidthLike,
} from "@CommonUtils/pog/shelfMerchandising";

interface ShelfSlot {
  bayIndex: number;
  shelf: { items: unknown[]; uniqueId: string };
  laneId: string;
  xLeftCm: number;
  xRightCm: number;
  liveWidth: number;
  /** Where the board is live along the run, in raw planogram units. */
  live: { from: number; to: number }[];
  /** The x range this board's packs read at when it is one level of a rack. */
  slice?: { from: number; to: number };
}

/** Keep stored total when unresized — width is a ceiling (5/2 rows measures 3, not 6). */
const totalFacingsFor = (
  base: Pick<PlanogramItemCommonNonCircular, "facings" | "facingsRows">,
  hFacings: number,
): number => {
  const rows = Math.max(base.facingsRows || 1, 1);
  const storedWidth = Math.ceil((base.facings || 0) / rows);
  return hFacings === storedWidth ? base.facings : hFacings * rows;
};

export const layoutToPog = (
  basePog: Pog,
  layout: GeneratedLayout,
  items: readonly StudioItem[],
  runIndex: number,
  unrangedSkus: readonly string[] = [],
): Pog => {
  const data = JSON.parse(JSON.stringify(basePog.toNonCircular())) as PogNonCircular;

  const baseBySku = new Map<string, PlanogramItemCommonNonCircular>();
  const homeShelfBySku = new Map<string, string>();
  for (const bay of data.planogram.bays) {
    for (const shelf of bay.shelves) {
      for (const item of shelf.items) {
        const key = item.productCode || item.id;
        if (!baseBySku.has(key)) {
          baseBySku.set(key, item);
          homeShelfBySku.set(key, shelf.uniqueId);
        }
      }
      shelf.items = [];
    }
  }

  // Slot x is raw planogram units, as pogToStudioItems reads it: the layout carries the same numbers.
  /* A segment's shelf is the WALL row the reader assigned (boards clustered by height), not an
     index within the bay: on a ragged wall bay A's second board can be row 4. */
  const rankOf = wallRowRankOf(basePog.planogram.bays);
  const rackOf = wallRackSlotOf(basePog.planogram.bays);
  const slotsByRank = new Map<number, ShelfSlot[]>();
  let xLeftCm = 0;
  data.planogram.bays.forEach((bay, bayIndex) => {
    bay.shelves.forEach((shelf, shelfIndex) => {
      const rank = rankOf(bayIndex, shelfIndex);
      const slots = slotsByRank.get(rank) ?? [];
      const rack = rackOf(bayIndex, shelfIndex);
      const step = rack ? (rack.to - rack.from) / rack.levels : 0;
      slots.push({
        slice: rack && {
          from: rack.from + rack.level * step,
          to: rack.from + (rack.level + 1) * step,
        },
        bayIndex,
        shelf,
        laneId: basePog.planogram.bays[bayIndex].shelves[shelfIndex].getFirstShelf().uniqueId,
        xLeftCm,
        xRightCm: xLeftCm + bay.width,
        liveWidth: getEffectiveMerchandisableWidth(shelf as unknown as ShelfWidthLike),
        live: getLiveSpans(shelf as unknown as ShelfWidthLike).map((span) => ({
          from: xLeftCm + span.x,
          to: xLeftCm + span.x + span.width,
        })),
      });
      slotsByRank.set(rank, slots);
    });
    xLeftCm += bay.width;
  });

  const itemsBySku = new Map(items.map((item) => [item.skuId, item]));
  const segments = Object.values(layout.placements)
    .flatMap((placement) =>
      placement.segments
        .filter((segment) => segment.run === runIndex)
        .map((segment, index) => ({ ...segment, skuId: placement.skuId, index })),
    )
    .sort((a, b) => a.shelf - b.shelf || a.xCm - b.xCm);

  const skipped: string[] = [];
  const keepAtHome = (skuId: string): boolean => {
    // Unplaceable items stay on the home shelf with base facings.
    const base = baseBySku.get(skuId);
    const homeId = homeShelfBySku.get(skuId);
    if (!base || !homeId) return false;
    for (const slots of slotsByRank.values()) {
      const slot = slots.find((candidate) => candidate.shelf.uniqueId === homeId);
      if (slot) {
        if (
          !slot.shelf.items.some(
            (it) => (it as { uniqueId?: string }).uniqueId === `${skuId}::home`,
          )
        ) {
          slot.shelf.items.push({ ...base, uniqueId: `${skuId}::home`, shelf: homeId });
        }
        return true;
      }
    }
    return false;
  };
  for (const segment of segments) {
    const base = baseBySku.get(segment.skuId);
    const studioItem = itemsBySku.get(segment.skuId);
    const slots = slotsByRank.get(segment.shelf);
    if (!base || !studioItem || !slots || slots.length === 0) {
      skipped.push(segment.skuId);
      keepAtHome(segment.skuId);
      continue;
    }
    const slotAt = (anchorX: number) => {
      const gapTo = (candidate: ShelfSlot) =>
        Math.max(candidate.xLeftCm - anchorX, anchorX - candidate.xRightCm + 1e-6, 0);
      const inSlice = (candidate: ShelfSlot) =>
        candidate.slice && anchorX >= candidate.slice.from && anchorX < candidate.slice.to ? 1 : 0;
      const liveAt = (candidate: ShelfSlot) =>
        !candidate.slice && candidate.live.some((span) => anchorX >= span.from && anchorX < span.to)
          ? 1
          : 0;
      const fit = (candidate: ShelfSlot) => [
        -gapTo(candidate),
        inSlice(candidate),
        liveAt(candidate),
        candidate.liveWidth,
      ];
      return slots.reduce((best, candidate) => {
        const [a, b] = [fit(candidate), fit(best)];
        const first = a.findIndex((value, index) => value !== b[index]);
        return first >= 0 && a[first] > b[first] ? candidate : best;
      });
    };
    const parts: { slot: ShelfSlot; firstFacing: number; hFacings: number }[] = [];
    for (let facing = 0; facing < segment.hFacings; facing++) {
      const slot = slotAt(segment.xCm + studioItem.facingWidthCm * (facing + 0.5));
      const last = parts[parts.length - 1];
      if (last?.slot.laneId === slot.laneId) last.hFacings++;
      else parts.push({ slot, firstFacing: facing, hFacings: 1 });
    }
    const totalFacings = totalFacingsFor(base, segment.hFacings);
    const unchanged = totalFacings === base.facings;
    let remaining = totalFacings;
    let assigned = 0;
    const rows = Math.max(base.facingsRows || 1, 1);
    parts.forEach(({ firstFacing, hFacings }, index) => {
      const slot = slotAt(segment.xCm + studioItem.facingWidthCm * (firstFacing + hFacings / 2));
      const facings = Math.min(remaining, hFacings * rows);
      remaining -= facings;
      const previous = assigned;
      assigned += facings;
      const share = (units: number | null | undefined) =>
        unchanged && units != null
          ? Math.floor((units * assigned) / totalFacings) -
            Math.floor((units * previous) / totalFacings)
          : null;
      slot.shelf.items.push({
        ...base,
        uniqueId: `${segment.skuId}::${segment.index}${parts.length > 1 ? `::${index}` : ""}`,
        facings,
        xCapNum: index === 0 ? base.xCapNum : 0,
        xCappedUnits: unchanged && index === 0 ? base.xCappedUnits : null,
        yCappedUnits: share(base.yCappedUnits),
        zCappedUnits: share(base.zCappedUnits),
        shelf: slot.shelf.uniqueId,
      });
    });
  }

  if (skipped.length > 0) {
    // eslint-disable-next-line no-console
    console.error(
      `layoutToPog: ${skipped.length} segment(s) could not be drawn where the layout put them; their SKUs were kept at their home shelf instead (spec §4 invariant 4): ${[...new Set(skipped)].slice(0, 5).join(", ")}${skipped.length > 5 ? "…" : ""}`,
    );
  }

  for (const skuId of unrangedSkus) {
    const base = baseBySku.get(skuId);
    if (!base) continue;
    if (data.unrangedItems.some((item) => (item.productCode || item.id) === skuId)) continue;
    const homeShelf = homeShelfBySku.get(skuId) ?? "";
    data.unrangedItems.unshift({
      ...base,
      shelf: homeShelf,
      potentialShelves: homeShelf ? [homeShelf] : [],
      reasons: [],
    });
  }

  return fitCapsToShelves(new Pog(data));
};

const WIDTH_EPS = 0.01;

/** The shelf a drawn position takes: its facings across, plus the caps laid beside them. */
export const drawnWidthOf = (item: {
  getHorizontalFacings: () => number;
  getWidth: () => number;
  getXCappingWidth: () => number;
}): number => Math.max(1, item.getHorizontalFacings()) * item.getWidth() + item.getXCappingWidth();

/**
 * A layout counts a position as its facings; the base position it is drawn from may also
 * carry x-caps, which the drawn shelf has to find room for. Where a stamped shelf would hold
 * more than its width, caps are laid down, widest first, until it fits: a cap is filler, and
 * a shelf over its width draws its packs on top of each other.
 */
export const fitCapsToShelves = (pog: Pog): Pog => {
  const trimmed = new Set<string>();
  for (const bay of pog.planogram.bays) {
    for (const shelf of bay.shelves) {
      // One lane per merged row, entered at its leftmost shelf.
      if (shelf.mergedLeft) continue;
      const lane: (typeof shelf)[] = [];
      for (let member: typeof shelf | null = shelf; member; member = member.mergedRight) {
        lane.push(member);
      }
      const capacity = lane.reduce((sum, member) => sum + member.width, 0);
      const positions = lane.flatMap((member) => member.items);
      let used = positions.reduce((sum, item) => sum + drawnWidthOf(item), 0);
      const capped = positions
        .filter((item) => item.getXCappingWidth() > 0)
        .sort((a, b) => b.getXCappingWidth() - a.getXCappingWidth());
      for (const item of capped) {
        if (used <= capacity + WIDTH_EPS) break;
        used -= item.getXCappingWidth();
        trimmed.add(item.uniqueId);
      }
    }
  }
  if (trimmed.size === 0) return pog;
  const data = pog.toNonCircular();
  for (const bay of data.planogram.bays) {
    for (const shelf of bay.shelves) {
      for (const item of shelf.items) {
        if (trimmed.has(item.uniqueId)) item.xCapNum = 0;
      }
    }
  }
  return new Pog(data);
};
